/**
 * MarkdownText — lightweight markdown renderer for AI chat bubbles.
 * No external dependencies. Handles everything LLMs typically output:
 *   **bold**, *italic*, ## headers, - bullets, 1. numbered lists,
 *   `inline code`, ⚠️ disclaimer lines, blank-line paragraphs.
 *
 * If the rendered content contains a legal disclaimer (detected automatically),
 * a "Find an Attorney" button is appended beneath the message, linking the user
 * to the /attorney-match feature.
 */
import { useNavigate } from 'react-router-dom'

// ── Disclaimer detector ────────────────────────────────────────────────────────
// Returns true if the content includes language that constitutes a legal
// disclaimer — i.e., "not legal advice", "consult a licensed attorney", etc.
const DISCLAIMER_PATTERNS = [
  /not\s+legal\s+advice/i,
  /consult\s+a\s+(licensed|qualified|family\s+law)\s+attorney/i,
  /not\s+a\s+lawyer/i,
  /coaching\s+tool[^.]*not\s+a\s+lawyer/i,
  /legal\s+education[^.]*not\s+legal\s+advice/i,
  /attorney\s+review\s+recommended/i,
  /always\s+consult[^.]*attorney/i,
  /speak\s+with[^.]*attorney/i,
  /not\s+a\s+substitute\s+for\s+a\s+licensed\s+attorney/i,
  /court\s+self.help\s+center/i,
]

function hasLegalDisclaimer(text) {
  if (!text) return false
  return DISCLAIMER_PATTERNS.some(re => re.test(text))
}

// ── Inline styles: bold, italic, inline code ──────────────────────────────────
function renderInline(text) {
  // Split on **bold**, *italic*, `code` markers
  const parts = []
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g
  let last = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index))
    }
    if (match[2] !== undefined) {
      parts.push(<strong key={match.index} className="font-semibold">{match[2]}</strong>)
    } else if (match[3] !== undefined) {
      parts.push(<em key={match.index}>{match[3]}</em>)
    } else if (match[4] !== undefined) {
      parts.push(
        <code key={match.index} className="bg-slate-200 text-slate-700 rounded px-1 py-0.5 text-xs font-mono">
          {match[4]}
        </code>
      )
    }
    last = match.index + match[0].length
  }

  if (last < text.length) {
    parts.push(text.slice(last))
  }

  return parts.length ? parts : text
}

// ── Block-level renderer ───────────────────────────────────────────────────────
export default function MarkdownText({ content, isUser = false, hideAttorneyButton = false }) {
  const navigate = useNavigate()

  if (!content) return null

  const showAttorneyButton = !isUser && !hideAttorneyButton && hasLegalDisclaimer(content)

  const lines   = content.split('\n')
  const blocks  = []
  let   listBuf = []   // pending list items
  let   listType = null // 'ul' | 'ol'
  let   key     = 0

  function flushList() {
    if (!listBuf.length) return
    const Tag = listType === 'ol' ? 'ol' : 'ul'
    const cls = listType === 'ol'
      ? 'list-decimal list-outside ml-4 space-y-1 my-2'
      : 'list-disc list-outside ml-4 space-y-1 my-2'
    blocks.push(
      <Tag key={key++} className={cls}>
        {listBuf.map((item, i) => (
          <li key={i} className="text-inherit leading-snug pl-0.5">
            {renderInline(item)}
          </li>
        ))}
      </Tag>
    )
    listBuf  = []
    listType = null
  }

  for (let i = 0; i < lines.length; i++) {
    const raw  = lines[i]
    const line = raw.trimEnd()

    // ── Heading: ## or ### ────────────────────────────────────────────────────
    const h2 = line.match(/^##\s+(.+)/)
    const h3 = line.match(/^###\s+(.+)/)
    if (h2 || h3) {
      flushList()
      const text = (h2 || h3)[1]
      const cls  = h2
        ? `font-bold text-base mt-3 mb-1 ${isUser ? 'text-white' : 'text-slate-800'}`
        : `font-semibold text-sm mt-2 mb-0.5 ${isUser ? 'text-white' : 'text-slate-700'}`
      blocks.push(<p key={key++} className={cls}>{renderInline(text)}</p>)
      continue
    }

    // ── Horizontal rule: --- or *** ───────────────────────────────────────────
    if (/^(\-{3,}|\*{3,})$/.test(line.trim())) {
      flushList()
      blocks.push(<hr key={key++} className="border-slate-200 my-2" />)
      continue
    }

    // ── Unordered list: - item or • item ──────────────────────────────────────
    const ulMatch = line.match(/^[\-\*•]\s+(.+)/)
    if (ulMatch) {
      if (listType === 'ol') flushList()
      listType = 'ul'
      listBuf.push(ulMatch[1])
      continue
    }

    // ── Ordered list: 1. item ─────────────────────────────────────────────────
    const olMatch = line.match(/^\d+\.\s+(.+)/)
    if (olMatch) {
      if (listType === 'ul') flushList()
      listType = 'ol'
      listBuf.push(olMatch[1])
      continue
    }

    // ── Flush any pending list before a non-list line ─────────────────────────
    flushList()

    // ── Blank line ────────────────────────────────────────────────────────────
    if (!line.trim()) {
      blocks.push(<div key={key++} className="h-1.5" />)
      continue
    }

    // ── Disclaimer line: starts with ⚠️ ──────────────────────────────────────
    if (line.startsWith('⚠️') || line.startsWith('⚠')) {
      blocks.push(
        <p key={key++} className={`text-xs mt-2 italic ${isUser ? 'text-blue-100' : 'text-slate-500'}`}>
          {renderInline(line)}
        </p>
      )
      continue
    }

    // ── Regular paragraph line ────────────────────────────────────────────────
    blocks.push(
      <p key={key++} className="leading-snug">
        {renderInline(line)}
      </p>
    )
  }

  // Flush any remaining list
  flushList()

  return (
    <div className="space-y-0.5 text-sm">
      {blocks}

      {/* ── Find an Attorney CTA ── */}
      {showAttorneyButton && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <button
            onClick={() => navigate('/attorney-match')}
            className="
              inline-flex items-center gap-2
              bg-brand-700 hover:bg-brand-800 active:scale-[0.97]
              text-white text-xs font-semibold
              px-4 py-2 rounded-xl
              transition-all duration-150 shadow-sm
            "
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Find an Attorney
          </button>
        </div>
      )}
    </div>
  )
}
